import { queryOptions } from "@tanstack/react-query";

import type { IndustryTaxonomyDto } from "app/pages/sales-intelligence/domain/industry/types/taxonomy";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";

import type { CrmJobsTransitionDto } from "../../types/crmJobsTransition";
import type { IndustryTransitionImpactReport, IndustryTransitionImpactReportDto } from "../../types/impactReport";
import { toImpactReport } from "../../utils/toImpactReport";
import { industryQueryKeys } from "../queryKeyFactory";

type Options<T = IndustryTaxonomyDto> = {
    select?: (data: IndustryTaxonomyDto) => T;
    enabled?: boolean;
};

/**
 * `gcTime: Infinity` on top of `staleTime: Infinity`: the taxonomy is large and indexing it is O(n),
 * and the default 5-minute garbage collection would force a refetch *and* a full re-index whenever
 * the user navigates away from the leads page for longer than that.
 */
export const createTaxonomyQuery = <T = IndustryTaxonomyDto>({ select, enabled }: Options<T> = {}) =>
    queryOptions<IndustryTaxonomyDto, Error, T>({
        enabled,
        queryKey: industryQueryKeys.taxonomy(),
        staleTime: Number.POSITIVE_INFINITY,
        gcTime: Number.POSITIVE_INFINITY,
        queryFn: ({ signal }) => salesApiService.industry.fetchTaxonomy({ cancellation: signal }),
        select,
    });

/**
 * `refetchOnMount` and `networkMode` override app defaults that would break this modal: `false` would
 * serve a cached report and issue no request on reopen, and `"online"` parks an offline query as
 * pending forever — no error, so the retry never appears.
 */
const IMPACT_REPORT_STALE_TIME_MS = 30 * 1000;
const IMPACT_REPORT_GC_TIME_MS = 5 * 60 * 1000;

const selectImpactReport = (dto: IndustryTransitionImpactReportDto): IndustryTransitionImpactReport =>
    toImpactReport(dto);

export const createImpactReportQuery = () =>
    queryOptions<IndustryTransitionImpactReportDto, Error, IndustryTransitionImpactReport>({
        queryKey: industryQueryKeys.impactReport(),
        queryFn: ({ signal }) => salesApiService.industry.fetchImpactReport({ cancellation: signal }),
        select: selectImpactReport,
        staleTime: IMPACT_REPORT_STALE_TIME_MS,
        gcTime: IMPACT_REPORT_GC_TIME_MS,
        refetchOnMount: true,
        networkMode: "always",
        retry: false,
    });

const CRM_JOBS_TRANSITION_STALE_TIME_MS = 30 * 1000;
const CRM_JOBS_TRANSITION_GC_TIME_MS = 5 * 60 * 1000;

type CreateCrmJobsTransitionQueryParams = {
    readonly enabled?: boolean;
};

export const createCrmJobsTransitionQuery = ({ enabled = true }: CreateCrmJobsTransitionQueryParams = {}) =>
    queryOptions<CrmJobsTransitionDto>({
        enabled,
        queryKey: industryQueryKeys.crmJobsTransition(),
        queryFn: ({ signal }) => salesApiService.industry.fetchCrmJobsTransition({ cancellation: signal }),
        staleTime: CRM_JOBS_TRANSITION_STALE_TIME_MS,
        gcTime: CRM_JOBS_TRANSITION_GC_TIME_MS,
        refetchOnMount: true,
        networkMode: "always",
        retry: false,
    });
